// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
// 5.9.25 - Added Firestore imports for user document creation
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
// 5.9.25 15:40 - Fixed firebase import path to match actual file location
// Error: Cannot find module '../firebase/firebaseConfig' 
// Actual file: src/firebase.js exports { db } - updated import path
import { db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const auth = getAuth();
  
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }
  
  // 5.9.25 - Enhanced signup to create Firestore user document with all required fields
  async function signup(email, password, userData = {}) {
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create Firestore user document with email as document ID
      const userDocData = {
        // Required fields for COOC platform
        role: 'user', // Default role for new users
        status: 'pending', // Status until verification complete
        createdAt: new Date().toISOString(),
        email: email,
        accountType: userData.accountType || 'ndis_participant',
        
        // Optional fields from signup form
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        mobile: userData.mobile || '',
        address: userData.address || {},
        
        // NDIS specific fields (if provided during signup)
        ndisNumber: userData.ndisNumber || '',
        planManager: userData.planManager || 'Self Managed',
        planManagerEmail: userData.planManagerEmail || '',
        
        // Platform settings
        subscriptionStatus: 'trial', // Or 'active' depending on your business model
        lastLogin: null
      };
      
      // Save to Firestore using email as document ID
      // Save to Firestore using email as document ID in Users collection
      await setDoc(doc(db, 'Users', email), userDocData);
      
      return userCredential;
    } catch (error) {
      console.error('Error during signup:', error);
      throw error;
    }
  }
  
  function logout() {
    return signOut(auth);
  }
  
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Fixed login for development/testing if needed
  function devLogin() {
    return signInWithEmailAndPassword(auth, 'jane.doe@cooc.com.au', 'test123');
  }
  
 // 5.9.25 - Enhanced to fetch user profile and role from Firestore using email
 useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    setCurrentUser(user);
    
    if (user) {
      try {
        // Use email as document ID (COOC system design)
        // 5.9.25 - Use correct collection name "Users" (capital U)
        const userDoc = await getDoc(doc(db, 'Users', user.email));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserProfile(userData);
          setUserRole(userData.role || 'user');
        } else {
          // User exists in Auth but not in Firestore - needs account setup
          console.log('User authenticated but no profile found - needs account setup');
          setUserRole('pending');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setUserRole('error');
      }
    } else {
      setUserProfile(null);
      setUserRole(null);
    }
    
    setLoading(false);
  });
  
  return unsubscribe;
}, []);
  
 // 5.9.25 - Added user profile and role to context value
 const value = {
  currentUser,
  userProfile,
  userRole,
  login,
  signup,
  logout,
  resetPassword,
  devLogin,
  error,
  setError
};
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}